/**
 * The Internal Audits read shapes and refs, as pure data (13-32), for `services/internalAuditService.ts` and
 * `scripts/record-read-proof.mjs --dry` (which measures each URL against the 2,048-character limit, ISS-261).
 *
 * - Ref: `AUD-<SP Id>` (D-12.1-04-12: no calculated `Reference` on this list); a practice audit is `AUD-P<n>`
 *   (the D-13-23-1 practice range: the `P` can never match a live ref).
 * - Reads select inside the expand, `fields($select=…)` (a `$select=fields/X` read 400s, and a swallowed 400 reads
 *   as empty). The list has no Person column the dashboard reads, so no `…LookupId` is needed.
 */

import { AUDIT_FIELDS, INTERNAL_AUDITS } from '../config/fieldset/internalAudits';
import { SHAREPOINT_SITE_DISPLAY_URL } from '../config/sharepoint';
import { RecordRefError, expandOf } from './recordQuery';

export const AUDIT_PREFIX = INTERNAL_AUDITS.prefix;

/** A live audit ref (leading zeros tolerated on input). */
export const AUDIT_REF_PATTERN = new RegExp(`^${AUDIT_PREFIX}-0*([1-9]\\d*)$`, 'i');
/** A practice audit ref (D-13-23-1). */
export const AUDIT_PRACTICE_REF_PATTERN = new RegExp(`^${AUDIT_PREFIX}-P([1-9]\\d*)$`, 'i');

export interface ParsedAuditRef {
  /** The SP item id (live) or the practice counter. */
  id: number;
  /** Canonical: `AUD-12` / `AUD-P3`. */
  ref: string;
  practice: boolean;
}

/** A live or practice audit ref, or `null`. */
export function readAuditRef(ref: unknown): ParsedAuditRef | null {
  if (typeof ref !== 'string') return null;
  const s = ref.trim();
  const live = AUDIT_REF_PATTERN.exec(s);
  if (live) {
    const id = Number(live[1]);
    return Number.isSafeInteger(id) ? { id, ref: `${AUDIT_PREFIX}-${id}`, practice: false } : null;
  }
  const practice = AUDIT_PRACTICE_REF_PATTERN.exec(s);
  if (practice) {
    const id = Number(practice[1]);
    return Number.isSafeInteger(id) ? { id, ref: `${AUDIT_PREFIX}-P${id}`, practice: true } : null;
  }
  return null;
}

/** `AUD-…` (live or practice). */
export function isAuditRef(ref: unknown): boolean {
  return readAuditRef(ref) !== null;
}

/** A live audit ref → `{ id, ref }`, or throws `unknown-ref` (a practice ref included). */
export function parseAuditRef(ref: unknown): ParsedAuditRef {
  const parsed = readAuditRef(ref);
  if (!parsed || parsed.practice) throw new RecordRefError(ref);
  return parsed;
}

/** `AUD-<id>` from the SP item id Graph returned. */
export function composeAuditRef(id: number | string): string {
  const n = Number(id);
  if (!Number.isSafeInteger(n) || n < 1) throw new RecordRefError(`${AUDIT_PREFIX}-${String(id)}`);
  return `${AUDIT_PREFIX}-${n}`;
}

/** `AUD-P<n>`, a practice audit ref. */
export function composePracticeAuditRef(n: number): string {
  if (!Number.isSafeInteger(n) || n < 1) throw new RecordRefError(`${AUDIT_PREFIX}-P${String(n)}`);
  return `${AUDIT_PREFIX}-P${n}`;
}

/**
 * The item's SharePoint form, where working papers are attached (D-13-L-6), the way 13-24's `recordEditFormUrl`
 * builds a register's; `null` for a practice audit (it has no item).
 */
export function auditEditFormUrl(ref: string): string | null {
  const parsed = readAuditRef(ref);
  if (!parsed || parsed.practice) return null;
  return `${SHAREPOINT_SITE_DISPLAY_URL}/Lists/${encodeURIComponent(INTERNAL_AUDITS.listTitle)}/EditForm.aspx?ID=${parsed.id}`;
}

/** Every Internal Audits column (§2.6), then `Created` and `Modified`: the record read and the one list read. */
export function auditSelect(): string[] {
  return [...new Set([...AUDIT_FIELDS.map((f) => f.name), 'Created', 'Modified'])];
}

/** The `$expand` both reads send. */
export function auditExpand(): string {
  return expandOf(auditSelect());
}
